import styled, { css } from 'styled-components'

type ExpandedProps = {
    $expanded?: boolean
}

type ActiveProps = {
    $active?: boolean
}

type AttachedProps = {
    $attached?: boolean
    $unavailable?: boolean
}

type ThumbProps = {
    $thumb?: string | null
    $thumbKey?: string | null
}

const hoverableSurface = css`
    transition:
        background-color 0.18s ease,
        color 0.18s ease,
        box-shadow 0.18s ease;
`

export const AppShell = styled.main`
    position: relative;
    min-height: 100vh;
    width: 100%;
    padding: 8px 0 16px;
    background: ${({ theme }) => theme.colors.background};
    overflow-x: clip;
    overflow-y: auto;
    border-radius: 0 0 10px 10px;
`

export const Toolbar = styled.section`
    position: sticky;
    top: 0;
    z-index: 10;
    padding: 0 8px 16px 16px;
    background: ${({ theme }) => theme.colors.background};
`

export const SelectContainer = styled.div`
    position: relative;
    margin-bottom: 8px;
    z-index: 2;
`

export const SelectTrigger = styled.button<ExpandedProps>`
    ${hoverableSurface};
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 40px;
    padding: 8px 10px 8px 8px;
    border-radius: ${({ theme }) => theme.radii.md};
    background: ${({ theme, $expanded }) => ($expanded ? theme.colors.overlayStrong : theme.colors.surface)};
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.colors.overlayStrong};
    }
`

export const SelectLeft = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`

export const PrimaryIcon = styled.img`
    display: block;
    width: 24px;
    height: 24px;
    flex: 0 0 auto;
`

export const MetaIcon = styled.img`
    display: block;
    width: 16px;
    height: 16px;
    opacity: 0.5;
`

export const SelectLabel = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
`

export const SelectMeta = styled.span`
    color: ${({ theme }) => theme.colors.textSoft};
    white-space: nowrap;
    font-size: 12px;
    font-weight: 500;

    @media (max-width: 320px) {
        display: none;
    }
`

export const Caret = styled.span<ExpandedProps>`
    display: inline-flex;
    align-items: center;
    color: rgba(51, 51, 51, 0.6);

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
        transition: transform 0.2s ease;
        transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
    }
`

export const SelectMenu = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    z-index: 31;
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.lg};
    background: #fff;
    box-shadow: ${({ theme }) => theme.shadows.floating};
`

export const SelectOption = styled.button<ActiveProps>`
    ${hoverableSurface};
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 44px;
    padding: 10px 14px;
    cursor: pointer;
    background: ${({ $active }) => ($active ? '#fafafa' : 'transparent')};

    &:hover {
        background: ${({ theme }) => theme.colors.overlayHover};
    }

    & + & {
        border-top: 1px solid ${({ theme }) => theme.colors.line};
    }
`

export const SearchRow = styled.div`
    position: relative;
    display: flex;
    align-items: flex-start;
`

export const SearchField = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 48px;
    padding: 0 56px 0 12px;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.md};
    background: ${({ theme }) => theme.colors.surface};
    overflow: hidden;

    &:focus-within {
        border-color: ${({ theme }) => theme.colors.focusLine};
        box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
    }
`

export const SearchIcon = styled.img`
    display: block;
    width: 24px;
    height: 24px;
    opacity: 0.45;
`

export const SearchInput = styled.input`
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    color: ${({ theme }) => theme.colors.textStrong};
    background: transparent;
    font-size: 14px;
    line-height: 1.3;
    font-weight: 400;

    &::placeholder {
        color: ${({ theme }) => theme.colors.textSoft};
    }
`

export const IconButton = styled.button`
    ${hoverableSurface};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 8px;
    border-radius: ${({ theme }) => theme.radii.md};
    background: #fff;
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.colors.overlayHover};
    }

    img {
        width: 24px;
        height: 24px;
    }
`

export const FilterButton = styled.button<ActiveProps>`
    ${hoverableSurface};
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 12px;
    background: #fff;
    box-shadow: ${({ theme }) => theme.shadows.filterButton};
    cursor: pointer;

    &:hover {
        background: ${({ theme, $active }) => ($active ? '#fff' : theme.colors.surfaceMuted)};
    }
`

export const FilterButtonInner = styled.img`
    width: 24px;
    height: 24px;
`

export const FilterPanel = styled.section`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 20;
    width: min(100%, 296px);
    border: 1px solid rgba(51, 51, 51, 0.1);
    border-radius: 20px;
    background: #fff;
    box-shadow: ${({ theme }) => theme.shadows.filterPanel};
    overflow: hidden;

    @media (max-width: 320px) {
        width: 100%;
    }
`

export const FilterPanelBody = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px 0;
`

export const FilterPanelSection = styled.div`
    display: flex;
    flex-direction: column;
`

export const FilterDivider = styled.div`
    height: 1px;
    margin: 12px 16px;
    background: ${({ theme }) => theme.colors.dividerStrong};
`

export const FilterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 16px;
`

export const FilterLabel = styled.span`
    color: ${({ theme }) => theme.colors.textStrong};
    font-size: 14px;
    font-weight: 500;
`

export const TimePills = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

export const TimePill = styled.button`
    display: inline-flex;
    align-items: center;
    min-width: 68px;
    min-height: 40px;
    padding: 0 12px;
    border-radius: ${({ theme }) => theme.radii.sm};
    background: ${({ theme }) => theme.colors.surfaceStrong};
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
    font-weight: 400;
    cursor: default;
`

const timeInputStyles = css`
    min-height: 40px;
    padding: 0 12px;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: ${({ theme }) => theme.radii.sm};
    background: ${({ theme }) => theme.colors.surfaceStrong};
    color: ${({ theme }) => theme.colors.textStrong};
    font-size: 14px;
    font-weight: 500;
`

export const TimeInput = styled.input`
    ${timeInputStyles};
    width: 86px;
`

export const DateInput = styled.input`
    ${timeInputStyles};
    width: 148px;
`

export const TimeDash = styled.span`
    width: 8px;
    height: 1px;
    background: ${({ theme }) => theme.colors.dividerStrong};
`

export const MinCapacitySelectWrap = styled.div`
    position: relative;
    width: 108px;

    &::before,
    &::after {
        position: absolute;
        right: 11px;
        color: rgba(51, 51, 51, 0.55);
        font-size: 8px;
        line-height: 1;
        pointer-events: none;
    }

    &::before {
        top: 11px;
        content: '▲';
    }

    &::after {
        bottom: 11px;
        content: '▼';
    }
`

export const MinCapacitySelect = styled.select`
    width: 100%;
    min-height: 40px;
    padding: 0 26px 0 12px;
    border: 0;
    border-radius: ${({ theme }) => theme.radii.sm};
    background: ${({ theme }) => theme.colors.surfaceStrong};
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
    font-weight: 400;
    appearance: none;
    cursor: pointer;

    &:focus {
        outline: 0;
        box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
    }
`

export const FilterTags = styled.div`
    padding: 0 16px;
`

export const FilterTagsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`

export const FilterClearButton = styled.button`
    color: ${({ theme }) => theme.colors.purple};
    font-size: 12px;
    line-height: 1.3;
    font-weight: 400;
    cursor: pointer;
`

export const TagList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
`

export const TagChip = styled.button<ActiveProps>`
    ${hoverableSurface};
    min-height: 24px;
    padding: 0 8px;
    border: 1px solid ${({ theme, $active }) => ($active ? 'rgba(255, 255, 255, 0.3)' : 'rgba(51, 51, 51, 0.05)')};
    border-radius: 10px;
    background: ${({ theme, $active }) => ($active ? theme.colors.purple : theme.colors.surfaceStrong)};
    color: ${({ theme, $active }) => ($active ? 'rgba(255, 255, 255, 0.88)' : theme.colors.text)};
    font-size: 14px;
    line-height: 1.3;
    font-weight: 400;
    cursor: pointer;
`

export const RoomsSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

export const AttachErrorMessage = styled.div`
    margin: 0 16px 10px;
    padding: 10px 12px;
    border-radius: ${({ theme }) => theme.radii.sm};
    background: ${({ theme }) => theme.colors.dangerBg};
    color: ${({ theme }) => theme.colors.danger};
    font-size: 13px;
    font-weight: 600;
`

export const EmptyState = styled.div`
    padding: 22px 16px 8px;
    color: ${({ theme }) => theme.colors.textSoft};
    font-size: 14px;
`

export const GroupSection = styled.section`
    overflow: hidden;
    border-radius: 10px;
`

export const GroupHeaderButton = styled.button`
    ${hoverableSurface};
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 34px;
    padding: 0 16px;
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.colors.overlaySubtle};
    }
`

export const GroupTitle = styled.span`
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
`

export const GroupBody = styled.div`
    display: grid;
    gap: 8px;
    padding-top: 8px;
`

export const RoomButton = styled.button<AttachedProps>`
    ${hoverableSurface};
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 64px;
    padding: 0 16px;
    border-radius: ${({ theme }) => theme.radii.lg};
    text-align: left;
    cursor: pointer;
    opacity: ${({ $unavailable }) => ($unavailable ? 0.6 : 1)};
    background: ${({ theme, $attached }) => ($attached ? theme.colors.attachedBg : 'transparent')};

    &:hover {
        background: ${({ theme, $attached, $unavailable }) =>
            $unavailable ? 'transparent' : $attached ? theme.colors.attachedBg : theme.colors.overlayHover};
    }

    &:disabled {
        cursor: ${({ $unavailable }) => ($unavailable ? 'not-allowed' : 'wait')};
    }
`

export const RoomThumb = styled.span<ThumbProps>`
    display: block;
    width: 48px;
    height: 48px;
    border-radius: ${({ theme }) => theme.radii.md};
    overflow: hidden;
    background: ${({ theme, $thumbKey }) =>
        ($thumbKey && theme.gradients[$thumbKey as keyof typeof theme.gradients]) || theme.gradients.barcelona};
    background-image: ${({ $thumb }) => ($thumb ? `url("${$thumb}")` : undefined)};
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
`

export const EmptyThumb = styled(RoomThumb)`
    display: grid;
    place-items: center;
    background: ${({ theme }) => theme.colors.emptyThumb};

    img {
        width: 24px;
        height: 24px;
    }
`

export const RoomContent = styled.div`
    min-width: 0;
`

export const RoomHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

export const RoomName = styled.h2`
    margin: 0;
    color: ${({ theme }) => theme.colors.textStrong};
    font-size: 14px;
    font-weight: 500;
    line-height: 1.2;
`

export const RoomMeta = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSoft};
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
`

export const RoomBadge = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 18px;
    margin-top: 4px;
    padding: 0 7px;
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.attachedBadgeBg};
    color: ${({ theme }) => theme.colors.purple};
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
`

export const RoomCapacity = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding-left: 8px;
    color: ${({ theme }) => theme.colors.textStrong};
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;

    img {
        width: 20px;
        height: 20px;
    }
`
